import moment from 'moment';
import { findMatchingDatePresetId, ORDER_LIST_DATE_RANGE_PRESETS } from '../../../../utils/order/orderListDateRangePresets';
import { normalizeCompareDateKey } from './statisticsCompareConfig';
import { formatComparePeriodLabel } from './statisticsComparePresets';

// 月份列表
export const STATISTICS_QUICK_PICK_MONTHS = Array.from({ length: 12 }, (_, index) => ({
	month: index,
	label: `${index + 1}月`,
}));

// 季度列表
export const STATISTICS_QUICK_PICK_QUARTERS = [
	{ quarter: 0, label: 'Q1' },
	{ quarter: 1, label: 'Q2' },
	{ quarter: 2, label: 'Q3' },
	{ quarter: 3, label: 'Q4' },
];

// 构建自然年列表
export const buildStatisticsQuickPickYears = (earliestYear, latestYear = moment().year()) => {
	const start = Math.min(Number(earliestYear), Number(latestYear));
	const end = Math.max(Number(earliestYear), Number(latestYear));
	if (!Number.isFinite(start) || !Number.isFinite(end)) return [];

	return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

/** API 未返回前的本地回退：与后端默认业务上线年 2024 对齐，这个方法就是兜底用的，避免前端报错 */
export const getStatisticsQuickPickYearsFallback = () => {
	const latestYear = moment().year();
	return buildStatisticsQuickPickYears(2024, latestYear);
};

export const STATISTICS_ROLLING_PERIOD_PRESETS = ORDER_LIST_DATE_RANGE_PRESETS;

/** 月份展示：单月「3月」；连续多月「1~12月」；跳选「1月、3月、5月」或「1~2月、5月」这种格式 */
export const formatMonthIndicesRangeLabel = (monthIndices = []) => {
	const sorted = [...new Set(monthIndices)].filter((month) => Number.isFinite(month) && month >= 0 && month <= 11).sort((a, b) => a - b);
	if (!sorted.length) return '';
	if (sorted.length === 1) return `${sorted[0] + 1}月`;

	const isConsecutive = sorted.every((month, index) => index === 0 || month === sorted[index - 1] + 1);
	if (isConsecutive) {
		return `${sorted[0] + 1}~${sorted[sorted.length - 1] + 1}月`;
	}

	const segments = [];
	let segmentStart = sorted[0];
	let segmentEnd = sorted[0];
	for (let index = 1; index < sorted.length; index += 1) {
		if (sorted[index] === segmentEnd + 1) {
			segmentEnd = sorted[index];
			continue;
		}
		segments.push([segmentStart, segmentEnd]);
		segmentStart = sorted[index];
		segmentEnd = sorted[index];
	}
	segments.push([segmentStart, segmentEnd]);

	return segments.map(([start, end]) => (start === end ? `${start + 1}月` : `${start + 1}~${end + 1}月`)).join('、');
};

// 构建月份范围
export const buildCalendarMonthRange = (year, monthIndex) => {
	const start = moment({ year, month: monthIndex, day: 1 });
	const end = start.clone().endOf('month');
	return {
		startDate: start.format('YYYY-MM-DD'),
		endDate: end.format('YYYY-MM-DD'),
		label: formatComparePeriodLabel(start, end),
	};
};

// 构建季度范围
export const buildCalendarQuarterRange = (year, quarterIndex) => {
	const startMonth = quarterIndex * 3;
	const start = moment({ year, month: startMonth, day: 1 });
	const end = start.clone().add(2, 'months').endOf('month');
	return {
		startDate: start.format('YYYY-MM-DD'),
		endDate: end.format('YYYY-MM-DD'),
		label: formatComparePeriodLabel(start, end),
	};
};

/** 自然年：当年为 1/1～今天，历史年为 1/1～12/31 */
export const buildCalendarYearRange = (year) => {
	const start = moment({ year, month: 0, day: 1 });
	const end = year === moment().year() ? moment().startOf('day') : moment({ year, month: 11, day: 31 });
	return {
		startDate: start.format('YYYY-MM-DD'),
		endDate: end.format('YYYY-MM-DD'),
		label: formatComparePeriodLabel(start, end),
	};
};

const rangesEqual = (left, right) =>
	normalizeCompareDateKey(left?.startDate) === normalizeCompareDateKey(right?.startDate) && normalizeCompareDateKey(left?.endDate) === normalizeCompareDateKey(right?.endDate);

export const matchCalendarMonth = (startDate, endDate, year, monthIndex) => rangesEqual({ startDate, endDate }, buildCalendarMonthRange(year, monthIndex));

export const matchCalendarQuarter = (startDate, endDate, year, quarterIndex) => rangesEqual({ startDate, endDate }, buildCalendarQuarterRange(year, quarterIndex));

export const matchCalendarYear = (startDate, endDate, year) => rangesEqual({ startDate, endDate }, buildCalendarYearRange(year));

const isConsecutiveMonthIndices = (monthIndices = []) => {
	const sorted = [...monthIndices].sort((a, b) => a - b);
	return sorted.length > 1 && sorted.every((month, index) => index === 0 || month === sorted[index - 1] + 1);
};

/** 同年内连续自然月包络（如 2026-01-01～2026-08-31 → 2026年1~8月）；须首尾对齐月初/月末 */
export const matchCalendarConsecutiveMonthSpan = (startDate, endDate) => {
	const start = moment(startDate);
	const end = moment(endDate);
	if (!start.isValid() || !end.isValid() || start.year() !== end.year()) return '';

	const monthIndices = deriveMonthIndicesSpanningRange(startDate, endDate);
	if (!isConsecutiveMonthIndices(monthIndices)) return '';

	const year = start.year();
	const envelopeStart = normalizeCompareDateKey(buildCalendarMonthRange(year, monthIndices[0]).startDate);
	const envelopeEnd = normalizeCompareDateKey(buildCalendarMonthRange(year, monthIndices[monthIndices.length - 1]).endDate);
	if (envelopeStart !== normalizeCompareDateKey(startDate) || envelopeEnd !== normalizeCompareDateKey(endDate)) {
		return '';
	}

	return `${year}年${formatMonthIndicesRangeLabel(monthIndices)}`;
};

/** 同一年内日期区间覆盖的月份下标（含首尾月，不要求整月对齐） */
export const deriveMonthIndicesSpanningRange = (startDate, endDate) => {
	const start = moment(startDate);
	const end = moment(endDate);
	if (!start.isValid() || !end.isValid() || start.year() !== end.year()) return [];

	const months = [];
	for (let monthIndex = start.month(); monthIndex <= end.month(); monthIndex += 1) {
		months.push(monthIndex);
	}
	return months;
};

export const resolveActiveQuickPickYear = (startDate, endDate, years = getStatisticsQuickPickYearsFallback()) => {
	const start = moment(startDate);
	if (start.isValid() && years.includes(start.year())) return start.year();
	const currentYear = moment().year();
	if (years.includes(currentYear)) return currentYear;
	return years[years.length - 1];
};

/** 日期区间覆盖的自然年（含首尾年） */
export const deriveYearsIncludedInRange = (startDate, endDate) => {
	const start = moment(startDate);
	const end = moment(endDate);
	if (!start.isValid() || !end.isValid()) return [];
	const firstYear = start.year();
	const lastYear = end.year();
	if (!Number.isFinite(firstYear) || !Number.isFinite(lastYear)) return [];
	const years = [];
	for (let year = firstYear; year <= lastYear; year += 1) {
		years.push(year);
	}
	return years;
};

/** 选期面板连续多选整年后的包络区间（如 2024~2026年）；仅支持组成/超期，不支持对比段分析 */
export const isStatisticsMultiYearEnvelopeOrderRange = (startDate, endDate) => {
	if (!startDate || !endDate) return false;

	const spanYears = deriveYearsIncludedInRange(startDate, endDate);
	if (spanYears.length <= 1) return false;

	const envelopeStart = normalizeCompareDateKey(buildCalendarYearRange(spanYears[0]).startDate);
	const envelopeEnd = normalizeCompareDateKey(buildCalendarYearRange(spanYears[spanYears.length - 1]).endDate);
	return envelopeStart === normalizeCompareDateKey(startDate) && envelopeEnd === normalizeCompareDateKey(endDate);
};

export const formatStatisticsMultiYearRangeLabel = (years = []) => {
	if (!years.length) return '';
	if (years.length === 1) return years[0] === moment().year() ? '本年' : `${years[0]}年`;
	const sorted = [...years].sort((a, b) => a - b);
	const isConsecutive = sorted.every((year, index) => index === 0 || year === sorted[index - 1] + 1);
	if (isConsecutive) return `${sorted[0]}~${sorted[sorted.length - 1]}年`;
	return sorted.map((year) => `${year}年`).join('、');
};

export const buildRollingPeriodRange = (preset) => {
	const { from, to } = preset.getRange();
	return {
		startDate: from,
		endDate: to,
		label: preset.label,
	};
};

export const matchRollingPeriodPresetId = (startDate, endDate) => {
	const from = normalizeCompareDateKey(startDate);
	const to = normalizeCompareDateKey(endDate);
	return findMatchingDatePresetId(from, to, STATISTICS_ROLLING_PERIOD_PRESETS);
};

/** 按指定「当日」还原本周/本月/本季/本年（识别保存时的滚动选期） */
const matchRollingPeriodPresetIdAsOf = (startDate, endDate, asOf) => {
	const asOfDate = asOf instanceof Date ? asOf : new Date(asOf);
	if (!startDate || !endDate || Number.isNaN(asOfDate.getTime())) return null;
	const from = normalizeCompareDateKey(startDate);
	const to = normalizeCompareDateKey(endDate);
	if (!from || !to) return null;
	for (const preset of STATISTICS_ROLLING_PERIOD_PRESETS) {
		const range = preset.getRange(asOfDate);
		if (range.from === from && range.to === to) return preset.id;
	}
	return null;
};

/**
 * 实时图：保存时的「本年/本月/本季/本周」滚到今天再查。
 * 仅当保存日与区间结束日同一天、且当时命中滚动快捷项时才滚，避免手选 1/1～3/10 被改掉。
 */
export const rebaseLiveRollingPeriodFilters = (filters = {}, snapshot = {}) => {
	if (snapshot?.refreshMode === 'frozen') return filters;

	const asOf = snapshot?.createdAt || snapshot?.pinnedAt || snapshot?.updatedAt;
	if (!asOf) return filters;
	if (normalizeCompareDateKey(asOf) !== normalizeCompareDateKey(filters.endDate)) return filters;

	const rollingId = matchRollingPeriodPresetIdAsOf(filters.startDate, filters.endDate, asOf);
	if (!rollingId) return filters;

	const preset = STATISTICS_ROLLING_PERIOD_PRESETS.find((item) => item.id === rollingId);
	if (!preset) return filters;
	const next = buildRollingPeriodRange(preset);
	if (
		normalizeCompareDateKey(next.startDate) === normalizeCompareDateKey(filters.startDate) &&
		normalizeCompareDateKey(next.endDate) === normalizeCompareDateKey(filters.endDate)
	) {
		return filters;
	}

	return { ...filters, startDate: next.startDate, endDate: next.endDate };
};

export const resolvePeriodQuickPickSelectionLabel = (startDate, endDate) => {
	const rollingId = matchRollingPeriodPresetId(startDate, endDate);
	if (rollingId) {
		const preset = STATISTICS_ROLLING_PERIOD_PRESETS.find((item) => item.id === rollingId);
		if (preset) return preset.label;
	}

	const start = moment(startDate);
	if (!start.isValid()) return '';

	if (isStatisticsMultiYearEnvelopeOrderRange(startDate, endDate)) {
		return formatStatisticsMultiYearRangeLabel(deriveYearsIncludedInRange(startDate, endDate));
	}

	const rangeYear = start.year();

	if (matchCalendarYear(startDate, endDate, rangeYear)) {
		return rangeYear === moment().year() ? '本年' : `${rangeYear}年`;
	}

	for (const { month, label } of STATISTICS_QUICK_PICK_MONTHS) {
		if (matchCalendarMonth(startDate, endDate, rangeYear, month)) return `${rangeYear}年${label}`;
	}
	for (const { quarter, label } of STATISTICS_QUICK_PICK_QUARTERS) {
		if (matchCalendarQuarter(startDate, endDate, rangeYear, quarter)) return `${rangeYear}年${label}`;
	}

	const consecutiveMonthSpanLabel = matchCalendarConsecutiveMonthSpan(startDate, endDate);
	if (consecutiveMonthSpanLabel) return consecutiveMonthSpanLabel;

	// 本年选期存的是「1/1～当日」；隔日不再等于 buildCalendarYearRange，仍应识别为规范选期
	const end = moment(endDate);
	if (
		rangeYear === moment().year() &&
		start.month() === 0 &&
		start.date() === 1 &&
		end.isValid() &&
		end.year() === rangeYear &&
		end.isSameOrBefore(moment(), 'day')
	) {
		return '本年';
	}

	return '';
};

/** 与选期按钮展示一致：快捷名优先，否则回退为「YYYY年」；无日期时回退本年口径文案 */
export const resolveStatisticsPeriodDisplayLabel = (startDate, endDate, years = getStatisticsQuickPickYearsFallback()) => {
	if (!startDate && !endDate) return '本年';
	const selectionSuffix = resolvePeriodQuickPickSelectionLabel(startDate, endDate);
	if (selectionSuffix) return selectionSuffix;
	const resolvedYear = resolveActiveQuickPickYear(startDate, endDate, years);
	return resolvedYear ? `${resolvedYear}年` : '';
};

/** 总览对比列右上角：自定义对比优先用已命名标签，其余与选期按钮一致 */
export const resolveComparePeriodHeaderLabel = ({ kind, title, periodStartDate, periodEndDate, periodLabel }) => {
	if (kind === 'anchor' || kind === 'custom') {
		const custom = title?.trim();
		if (custom) return custom;
	}
	const friendly = resolveStatisticsPeriodDisplayLabel(periodStartDate, periodEndDate);
	if (friendly) return friendly;
	return periodLabel?.trim() || '—';
};
